import { dependencies } from '@pn/core/dependencies';
import type { Company } from '@pn/core/domain/intel/company';
import { useBuildSliceSelector } from '@pn/services/redux/useBuildSliceSelector';
import { createSelector } from '@reduxjs/toolkit';
import { isNil } from 'lodash-es';
import { companiesSlice, type CompaniesStorageState } from './companiesSlice';

export const useCompaniesStorage = () => {
  const { name, getInitialState } = companiesSlice;

  const useSliceSelector = useBuildSliceSelector(name, getInitialState());

  return {
    isFetching: useSliceSelector((state) => state.isFetching),
    isError: useSliceSelector((state) => state.isError),
    companies: useSliceSelector((state) => state.companies),
    checkedCompanies: useSliceSelector(selectCheckedCompanies),
    checkedCompanyIds: useSliceSelector((state) => state.checkedCompanyIds),
    checkedCompaniesColors: useSliceSelector(
      (state) => state.checkedCompaniesColors
    ),
    selectedCompany: useSliceSelector(selectSelectedCompany),
    selectedCompanyId: useSliceSelector((state) => state.selectedCompanyId),
    companyNameToIdRecord: useSliceSelector(
      (state) => state.companyNameToIdRecord
    ),
    landBrokers: useSliceSelector(selectLandBrokerCompanies),
    recentlyViewedCompanies: useSliceSelector(selectRecentlyViewedCompanies),
  };
};

export const companiesActions = () => {
  const { dispatch, getState } = dependencies.store;
  const { actions, name } = companiesSlice;

  if (isNil(getState()[name])) {
    throw new Error(`[${name}] reducer has not been injected yet`);
  }

  return {
    request: () => dispatch(actions.request()),
    receive: (companies: Company[]) => dispatch(actions.receive(companies)),
    error: () => dispatch(actions.error()),
    reset: () => dispatch(actions.reset()),
    load: (company: Company | Company[]) => dispatch(actions.load(company)),
    check: (companyIds: Company['id'][]) => dispatch(actions.check(companyIds)),
    uncheck: (companyIds: Company['id'][]) =>
      dispatch(actions.uncheck(companyIds)),
    setCheckedCompanyColor: (companyId: Company['id'], color: string) =>
      dispatch(actions.setCheckedCompanyColor({ companyId, color })),
    select: (companyId: Company['id']) => dispatch(actions.select(companyId)),
    update: (companyId: Company['id'], company: Company) =>
      dispatch(actions.update({ companyId, company })),
    markAsRecentlyViewed: (company: Company | Company[]) =>
      dispatch(actions.markAsRecentlyViewed(company)),
  };
};

const selectCheckedCompanies = createSelector(
  [
    (state: CompaniesStorageState) => state.companies,
    (state: CompaniesStorageState) => state.checkedCompanyIds,
    (state: CompaniesStorageState) => state.checkedCompaniesColors,
  ],
  (companies, checkedCompanyIds, checkedCompaniesColors) => {
    return companies
      .filter(({ id }) => checkedCompanyIds.includes(id))
      .map((company) => {
        const color = checkedCompaniesColors[company.id] ?? 'rgba(0,0,0,0.5)';
        return {
          ...company,
          color: color,
        };
      });
  }
);

const selectSelectedCompany = createSelector(
  [
    (state: CompaniesStorageState) => state.companies,
    (state: CompaniesStorageState) => state.selectedCompanyId,
  ],
  (companies, selectedCompanyId) =>
    companies.find(({ id }) => id === selectedCompanyId)
);

const selectLandBrokerCompanies = createSelector(
  [(state: CompaniesStorageState) => state.companies],
  (companies) => companies.filter(({ purpose }) => purpose === 'land_broker')
);

const selectRecentlyViewedCompanies = createSelector(
  [
    (state: CompaniesStorageState) => state.companies,
    (state: CompaniesStorageState) => state.recentlyViewedCompanyIds,
  ],
  (companies, selectedCompanyId) =>
    companies.filter(({ id }) => selectedCompanyId.includes(id))
);
